import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

export interface MultiSelectGroup {
  heading: string;
  options: Array<{ value: string; label: string }>;
}

interface SearchableMultiSelectProps {
  values: string[];
  onValuesChange: (values: string[]) => void;
  placeholder: string;
  countLabel: string;
  groups: MultiSelectGroup[];
  triggerWidth?: string;
  dropdownWidth?: string;
}

export function SearchableMultiSelect({
  values,
  onValuesChange,
  placeholder,
  countLabel,
  groups,
  triggerWidth = "140px",
  dropdownWidth = "240px",
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const isActive = values.length > 0;
  const triggerLabel = isActive ? `${values.length} ${countLabel}` : placeholder;

  function toggle(value: string) {
    onValuesChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
  }

  // Click a group heading: select all its options, or deselect all if already complete
  function toggleGroup(group: MultiSelectGroup) {
    const groupValues = group.options.map((o) => o.value);
    const allSelected = groupValues.every((v) => values.includes(v));
    onValuesChange(
      allSelected
        ? values.filter((v) => !groupValues.includes(v))
        : [...new Set([...values, ...groupValues])]
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-7 items-center justify-between gap-1.5 rounded-md border border-black/15 bg-white px-2.5 text-[13px] transition-colors hover:bg-gray-50",
            isActive &&
              "border-[var(--brand-dark-teal)] font-semibold text-[var(--brand-dark-teal)]"
          )}
        >
          <span className="truncate" style={{ maxWidth: triggerWidth }}>
            {triggerLabel}
          </span>
          <ChevronsUpDown className="size-3 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: dropdownWidth }} align="start">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {groups.map((group) => {
              const allSelected = group.options.every((o) => values.includes(o.value));
              return (
                <CommandGroup
                  key={group.heading}
                  heading={
                    <button
                      type="button"
                      onClick={() => toggleGroup(group)}
                      className={cn(
                        "flex w-full cursor-pointer items-center gap-1 rounded-sm px-1 py-1 -my-0.5 text-left transition-colors hover:bg-black/5 hover:text-[var(--brand-dark-teal)]",
                        allSelected && "text-[var(--brand-dark-teal)] font-semibold"
                      )}
                      title={allSelected ? "Deselect all" : "Select all"}
                    >
                      <Check
                        className={cn("size-3 shrink-0", allSelected ? "opacity-100" : "opacity-0")}
                      />
                      {group.heading}
                    </button>
                  }
                >
                  {group.options.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      onSelect={() => toggle(opt.value)}
                    >
                      <Check
                        className={cn(
                          "size-3 shrink-0",
                          values.includes(opt.value) ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {opt.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
